import { ApiError } from '@shared/api/client';
import { isPermissionRefusal, resolveErrorCopy, type ErrorCopy } from '@shared/errors/catalog';

import { ErrorBanner } from './ErrorBanner';

/**
 * One failed read, rendered against its own band.
 *
 * **A panel-level failure never blanks a page.** A detail route that composes
 * three independent reads takes one panel down and leaves the rest usable; a
 * register takes its table down and keeps its head and its actions.
 *
 * The tone comes from the **resolved copy**, never from the call site: a `502`
 * is neutral because the failure is upstream and nobody in this console caused
 * it, and a danger-styled banner over a peer outage teaches an operator that
 * they broke something. Danger is reserved for a destructive action that
 * failed.
 *
 * Promoted to the shared ring on its **third** occurrence — the subscription
 * area wrote it, the account register copied it, and the credited-person
 * surfaces are the third. The two earlier copies still carry their own and are
 * due to collapse onto this one; that is a follow-up rather than a silent
 * rewrite of two features mid-flight.
 */
export function SurfaceError({
  error,
  catalog,
  onRetry,
}: {
  error: unknown;
  /** The feature's own `error_code` map, tried before the shared catalog. */
  catalog: Readonly<Record<string, ErrorCopy>>;
  onRetry: () => void;
}) {
  const status = error instanceof ApiError ? error.status : 0;
  const code = error instanceof ApiError ? error.code : undefined;
  const resolved = resolveErrorCopy(code, status, catalog);

  return (
    <ErrorBanner
      message={resolved.message}
      tone={resolved.tone === 'danger' ? 'danger' : 'neutral'}
      kind={isPermissionRefusal(code, status) ? 'permission' : 'failure'}
      onRetry={onRetry}
    />
  );
}
